import { Configuration, LogLevel } from '@azure/msal-browser';

// These three identifiers are constants for PTC's single-tenant statetax app and are NOT secrets —
// they already ship in the browser bundle. Hardcoding them as fallbacks means a build that forgot to
// set the VITE_ vars can no longer bake `login.microsoftonline.com/undefined` into the bundle
// (the 2026-07-17 AADSTS900023 outage: the deployed build was missing VITE_AZURE_TENANT_ID and
// VITE_AZURE_API_CLIENT_ID, so MSAL hit an "undefined" tenant/audience). Env still wins when present.
const PTC_TENANT_ID = 'f4eb3947-80d2-4603-ba59-aa333e0675a0';
const STATETAX_SPA_CLIENT_ID = 'd81abc94-cad6-48a3-ae94-eba6f200ca18';
const STATETAX_API_CLIENT_ID = 'd81abc94-cad6-48a3-ae94-eba6f200ca18';

// Treat an unset var — or the literal string "undefined" that Vite inlines for a missing VITE_ var —
// as absent, and use the constant instead.
export function resolveEnvId(value: string | undefined, fallback: string): string {
  return value && value !== 'undefined' ? value : fallback;
}

const tenantId = resolveEnvId(import.meta.env.VITE_AZURE_TENANT_ID, PTC_TENANT_ID);
const clientId = resolveEnvId(import.meta.env.VITE_AZURE_CLIENT_ID, STATETAX_SPA_CLIENT_ID);
const apiClientId = resolveEnvId(import.meta.env.VITE_AZURE_API_CLIENT_ID, STATETAX_API_CLIENT_ID);

// The fallbacks keep auth working, but a build that ships without the VITE_AZURE_* vars is still a
// misconfiguration worth surfacing — flag it in the console instead of failing silently, so the next
// deploy built with an incomplete env is caught in DevTools rather than by a locked-out user.
if (
  !import.meta.env.VITE_AZURE_TENANT_ID ||
  import.meta.env.VITE_AZURE_TENANT_ID === 'undefined' ||
  !import.meta.env.VITE_AZURE_API_CLIENT_ID ||
  import.meta.env.VITE_AZURE_API_CLIENT_ID === 'undefined'
) {
  console.warn(
    '[authConfig] one or more VITE_AZURE_* build vars were missing; using built-in PTC constants. ' +
    'The deploy build was missing .env.production.local values — verify them before the next build.',
  );
}

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
  },
  cache: {
    // S11 [SEC-D]: sessionStorage reduces XSS token-theft persistence vs localStorage.
    // Tokens are cleared when the tab closes; the user re-authenticates on next open.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

// The only scope the SPA actually needs: an access token for our own backend
// (audience = STATETAX_API_CLIENT_ID). The backend validates this token and
// calls Dataverse itself with app credentials, so the SPA never needs a
// delegated Dataverse token.
export const apiScopes = [
  `api://${apiClientId}/access_as_user`,
];

// Consent for + obtain the API token at login, so it's cached up front.
// Previously this requested only the Dataverse user_impersonation scope (which
// the SPA never calls) — leaving acquireTokenSilent({ scopes: apiScopes }) to
// silently mint a token for a scope that was never part of the login consent.
// When that silent call failed, getAccessToken returned null and every API
// request hit the backend with no Authorization header ("No authorization
// header" 401 — the menus-load-then-fail outage).
export const loginRequest = {
  scopes: apiScopes,
};
